import { useCallback, useEffect, useRef, useState } from "react";

// Unified web refresh: runs the loader on mount + whenever deps change, and
// refetches fresh data from the backend when the tab/window regains focus
// (stale-while-refresh — existing data stays visible, no full page reload).
export function useAutoRefresh(loader, deps = []) {
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const loaderRef = useRef(loader);
  loaderRef.current = loader;
  const busy = useRef(false);

  const run = useCallback(async () => {
    if (busy.current) return;
    busy.current = true;
    setRefreshing(true);
    try {
      await loaderRef.current();
      setLastUpdated(Date.now());
    } catch (e) {
      // keep whatever data is already shown; surface handled by the page
    } finally {
      busy.current = false;
      setRefreshing(false);
    }
  }, []);

  // initial load + whenever deps change
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { run(); }, deps);

  // refetch when the user comes back to the tab/window (throttled)
  useEffect(() => {
    let last = Date.now();
    const trigger = () => {
      if (document.visibilityState === "hidden") return;
      if (Date.now() - last < 8000) return;
      last = Date.now();
      run();
    };
    window.addEventListener("focus", trigger);
    document.addEventListener("visibilitychange", trigger);
    return () => {
      window.removeEventListener("focus", trigger);
      document.removeEventListener("visibilitychange", trigger);
    };
  }, [run]);

  return { refreshing, lastUpdated, refresh: run };
}
